import React, { useCallback, useMemo, useState } from 'react'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/styles'

import { ReactComponent as CircleIcon } from '../../../assets/images/circle.svg'
import { useGetActivitiesQuery } from '../../../shared/queries/useGetActivitiesQuery'
import { EmptyPlaceholder } from '../../../shared/components/EmptyPlaceholder'
import { SimpleLegend } from '../../../shared/components/DonutCharts/components/SimpleLegend'
import { useIsMobileDevice } from '../../../shared/hooks/useIsMobileDevice'
import useActivitiesDisplayName from '../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import { DonutLegendItem } from '../../../shared/components/DonutCharts'
import { GANTT_CHART_COLORS } from '../../../shared/components/GanttChart/useGanttChartOptions'
import GanttChartHeader from '../../../shared/components/GanttChart/GanttChartHeader'

import { useTppData } from '../hooks/useTppData'
import { useTppGanttData } from '../hooks/useTppGanttData'
import useTppGanttChartOptions from '../hooks/useTppGanttChartOptions'

interface TppGanttChartProps {
    tppId: string | null | undefined
    selectedColumnId?: string | null
    drillDownSectionId?: string | null
    highlightedOnly?: boolean
}

const TppGanttChart = ({ tppId, selectedColumnId, drillDownSectionId, highlightedOnly }: TppGanttChartProps) => {
    const theme = useTheme()
    const isMobileDevice = useIsMobileDevice()
    const activitiesDisplayName = useActivitiesDisplayName()

    const { matrixState, isLoading: isMatrixLoading } = useTppData(tppId)
    const { data: activities, isLoading: isActivitiesLoading } = useGetActivitiesQuery()

    const { activitiesBySection, sections } = useTppGanttData(
        matrixState,
        activities,
        isActivitiesLoading,
        selectedColumnId,
        drillDownSectionId,
        highlightedOnly
    )

    // Track hidden section IDs
    const [hiddenSectionIds, setHiddenSectionIds] = useState<Set<string>>(new Set())

    // Derive valid hidden IDs — drop any that aren't in the current sections
    // so stale IDs from a previous drill-down/column never hide current sections.
    // Also prevent hiding ALL sections (matches the guard in handleToggleSection).
    const validHiddenSectionIds = useMemo(() => {
        if (hiddenSectionIds.size === 0) return hiddenSectionIds
        const currentIds = new Set(sections.map((s) => s.id))
        const valid = new Set<string>()
        hiddenSectionIds.forEach((id) => {
            if (currentIds.has(id)) valid.add(id)
        })
        // If every current section would be hidden, show them all instead
        if (valid.size >= currentIds.size) return new Set<string>()
        return valid.size === hiddenSectionIds.size ? hiddenSectionIds : valid
    }, [hiddenSectionIds, sections])

    // Add colors to sections based on original index
    const sectionsWithColors = useMemo(
        () =>
            sections.map((section, index) => ({
                ...section,
                color: GANTT_CHART_COLORS[index % GANTT_CHART_COLORS.length] || theme.palette.figma.grayscale.tier4,
            })),
        [sections, theme.palette.figma.grayscale.tier4]
    )

    // Filter sections based on visibility (colors stay consistent)
    const filteredSections = useMemo(
        () => sectionsWithColors.filter((s) => !validHiddenSectionIds.has(s.id)),
        [sectionsWithColors, validHiddenSectionIds]
    )

    const filteredActivitiesBySection = useMemo(() => {
        const filtered: Record<string, (typeof activitiesBySection)[string]> = {}
        for (const sectionId of Object.keys(activitiesBySection)) {
            if (!validHiddenSectionIds.has(sectionId)) {
                filtered[sectionId] = activitiesBySection[sectionId]
            }
        }
        return filtered
    }, [activitiesBySection, validHiddenSectionIds])

    const ganttChartOptions = useTppGanttChartOptions(filteredActivitiesBySection, filteredSections, true)

    // Both counts are section-activity pairs rather than distinct activities: an
    // activity linked across sections is drawn once per section, so counting bars
    // keeps the numerator and denominator on the same basis. Sections hidden via
    // the legend drop out of both, since they're filtered out of the data.
    const totalCount = useMemo(
        () => Object.values(filteredActivitiesBySection).reduce((total, list) => total + (list?.length || 0), 0),
        [filteredActivitiesBySection]
    )

    // Build legend items from ALL sections (not filtered) so user can toggle them back
    const legendItems = useMemo(
        (): DonutLegendItem[] =>
            sectionsWithColors.map((section) => ({
                id: section.id,
                label: section.title,
                color: section.color,
                icon: CircleIcon,
            })),
        [sectionsWithColors]
    )

    // Build hiddenItems object for SimpleLegend
    const hiddenItems = useMemo(() => {
        const hidden: Record<string, boolean> = {}
        legendItems.forEach((item) => {
            hidden[item.label] = validHiddenSectionIds.has(item.id)
        })
        return hidden
    }, [legendItems, validHiddenSectionIds])

    const handleToggleSection = useCallback(
        (index: number) => {
            const section = sectionsWithColors[index]
            if (!section) return

            setHiddenSectionIds((prev) => {
                const next = new Set(prev)
                if (next.has(section.id)) {
                    next.delete(section.id)
                } else {
                    // Don't allow hiding all sections
                    if (next.size < sectionsWithColors.length - 1) {
                        next.add(section.id)
                    }
                }
                return next
            })
        },
        [sectionsWithColors]
    )

    const isLoading = isMatrixLoading || isActivitiesLoading
    const isGanttChartDataEmpty = !ganttChartOptions

    if (isLoading) {
        return null
    }

    return (
        <Stack flex={1} direction="column" gap={1} px={2} py={2.75} bgcolor="figma.grayscale.tier7" borderRadius="16px">
            {isGanttChartDataEmpty && (
                <EmptyPlaceholder
                    title=""
                    subtitle={
                        // The header (and its tooltip) isn't rendered when nothing is
                        // drawable, so the reason has to live here instead.
                        totalCount > 0
                            ? `0/${totalCount} ${activitiesDisplayName.plural} showing — none have both a start and an end date`
                            : `No ${activitiesDisplayName.plural} linked to TPP cells`
                    }
                />
            )}
            {!isGanttChartDataEmpty && (
                <>
                    <GanttChartHeader series={ganttChartOptions?.series} totalCount={totalCount}>
                        <SimpleLegend
                            items={legendItems}
                            hiddenItems={hiddenItems}
                            onItemClick={handleToggleSection}
                            direction={isMobileDevice ? 'column' : 'row'}
                        />
                    </GanttChartHeader>
                    <Stack flex={1} direction="column">
                        <HighchartsReact
                            key={[
                                selectedColumnId ?? 'all',
                                drillDownSectionId ?? 'none',
                                highlightedOnly ? '1' : '0',
                                Array.from(validHiddenSectionIds).sort().join(','),
                            ].join('|')}
                            highcharts={Highcharts}
                            constructorType="ganttChart"
                            options={ganttChartOptions}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    )
}

export default TppGanttChart
